import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { ContentCard } from '../components/ContentCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Browse() {
  const [liveContent, setLiveContent] = useState([]);
  const [vodContent, setVodContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [liveRes, vodRes] = await Promise.all([
        axios.get(`${API}/content/live`),
        axios.get(`${API}/content/vod`)
      ]);
      
      setLiveContent(liveRes.data);
      setVodContent(vodRes.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const allContent = [...liveContent, ...vodContent];
  const matchContent = allContent.filter(c => c.category === 'match');
  const culturalContent = allContent.filter(c => c.category === 'cultural');
  const highlightContent = allContent.filter(c => c.category === 'highlight');

  const getContentByTab = () => {
    switch(activeTab) {
      case 'matches': return matchContent;
      case 'cultural': return culturalContent;
      case 'highlights': return highlightContent;
      default: return allContent;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4" data-testid="browse-title">
              BROWSE CONTENT
            </h1>
            <p className="text-muted-foreground text-lg">
              Discover matches, highlights, and cultural shows
            </p>
          </motion.div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <TabsList className="bg-muted border border-white/10" data-testid="browse-tabs">
              <TabsTrigger value="all" className="data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="tab-all">
                All Content
              </TabsTrigger>
              <TabsTrigger value="matches" className="data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="tab-matches">
                Matches
              </TabsTrigger>
              <TabsTrigger value="cultural" className="data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="tab-cultural">
                Cultural
              </TabsTrigger>
              <TabsTrigger value="highlights" className="data-[state=active]:bg-primary data-[state=active]:text-black" data-testid="tab-highlights">
                Highlights
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Live Section */}
          {liveContent.length > 0 && (
            <div className="mb-12" data-testid="live-section">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-3 h-3 bg-secondary rounded-full animate-pulse"></div>
                <h2 className="font-heading text-2xl text-white">LIVE NOW</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {liveContent.map((content) => (
                  <ContentCard key={content.id} content={content} />
                ))}
              </div>
            </div>
          )}

          {/* Content Grid */}
          {loading ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="loading-state">
              Loading content...
            </div>
          ) : getContentByTab().length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="empty-state">
              No content available
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="content-grid">
              {getContentByTab()
                .filter(c => !c.is_live)
                .map((content) => (
                  <ContentCard key={content.id} content={content} />
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}